<template>
  <div
    ref="viewer"
    class="help-pdf-viewer"
    :class="{ 'help-pdf-viewer--fullscreen': isFullscreen }"
    tabindex="0"
    @contextmenu.prevent
    @dragstart.prevent
  >
    <header class="help-pdf-toolbar">
      <div class="help-pdf-title" :title="subtitle || title">
        <span class="help-pdf-title-icon" aria-hidden="true">
          <i class="fa-regular fa-file-pdf"></i>
        </span>
        <span class="help-pdf-title-copy">
          <strong>{{ title }}</strong>
          <small v-if="subtitle">{{ subtitle }}</small>
        </span>
      </div>

      <div class="help-pdf-navigation" aria-label="Navigazione documento PDF">
        <button
          type="button"
          title="Pagina precedente"
          aria-label="Pagina precedente"
          :disabled="currentPage <= 1 || loading"
          @click="goToPage(currentPage - 1)"
        >
          <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
        </button>
        <span class="help-pdf-page-number">{{ currentPage }}</span>
        <span class="help-pdf-page-total">/ {{ totalPages || "-" }}</span>
        <button
          type="button"
          title="Pagina successiva"
          aria-label="Pagina successiva"
          :disabled="currentPage >= totalPages || loading"
          @click="goToPage(currentPage + 1)"
        >
          <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>

      <div class="help-pdf-actions">
        <button
          type="button"
          title="Riduci zoom"
          aria-label="Riduci zoom"
          :disabled="zoom <= 0.6 || loading"
          @click="zoomOut"
        >
          <i class="fa-solid fa-minus" aria-hidden="true"></i>
        </button>
        <span class="help-pdf-zoom">{{ Math.round(zoom * 100) }}%</span>
        <button
          type="button"
          title="Aumenta zoom"
          aria-label="Aumenta zoom"
          :disabled="zoom >= 2.2 || loading"
          @click="zoomIn"
        >
          <i class="fa-solid fa-plus" aria-hidden="true"></i>
        </button>
        <span class="help-pdf-divider" aria-hidden="true"></span>
        <button
          type="button"
          :title="isFullscreen ? 'Esci da schermo intero' : 'Schermo intero'"
          :aria-label="
            isFullscreen ? 'Esci da schermo intero' : 'Apri a schermo intero'
          "
          @click="toggleFullscreen"
        >
          <i
            :class="
              isFullscreen ? 'fa-solid fa-compress' : 'fa-solid fa-expand'
            "
            aria-hidden="true"
          ></i>
        </button>
        <template v-if="closable">
          <span class="help-pdf-divider" aria-hidden="true"></span>
          <button
            type="button"
            class="help-pdf-close"
            title="Chiudi documento"
            aria-label="Chiudi documento"
            @click="$emit('close')"
          >
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </template>
      </div>
    </header>

    <main ref="scrollArea" class="help-pdf-scroll-area" @scroll="handleScroll">
      <div v-if="loading" class="help-pdf-status" role="status">
        <span class="help-pdf-spinner" aria-hidden="true"></span>
        <span>Caricamento PDF...</span>
      </div>

      <div v-else-if="error" class="help-pdf-status help-pdf-status--error">
        <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
        <span>{{ error }}</span>
      </div>

      <div v-else class="help-pdf-pages">
        <section
          v-for="page in pages"
          :key="page.number"
          :ref="`pageWrap${page.number}`"
          class="help-pdf-page"
          :style="pageStyle(page)"
          :aria-label="`Pagina ${page.number} di ${totalPages}`"
        >
          <canvas :ref="`pageCanvas${page.number}`"></canvas>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: "HelpPdfViewer",
  props: {
    src: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: "Documento PDF",
    },
    subtitle: {
      type: String,
      default: "",
    },
    closable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pdfDocument: null,
      loadingTask: null,
      pages: [],
      totalPages: 0,
      currentPage: 1,
      zoom: 1,
      loading: false,
      error: "",
      renderTasks: {},
      renderedPages: {},
      renderingPages: {},
      renderVersion: 0,
      loadVersion: 0,
      scrollTicking: false,
      isFullscreen: false,
    };
  },
  watch: {
    src() {
      this.loadPdf();
    },
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    this.loadPdf();
  },
  beforeDestroy() {
    this.loadVersion += 1;
    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange
    );
    this.disposeDocument();
  },
  methods: {
    async loadPdf() {
      const loadVersion = ++this.loadVersion;
      this.loading = true;
      this.error = "";
      this.currentPage = 1;
      this.zoom = 1;
      this.pages = [];
      this.totalPages = 0;
      await this.disposeDocument();

      try {
        this.loadingTask = pdfjsLib.getDocument(this.src);
        const document = await this.loadingTask.promise;
        if (loadVersion !== this.loadVersion) {
          await document.destroy();
          return;
        }

        this.pdfDocument = document;
        this.totalPages = document.numPages;
        const firstPage = await document.getPage(1);
        const firstViewport = firstPage.getViewport({ scale: 1 });
        firstPage.cleanup();
        this.pages = Array.from({ length: this.totalPages }, (_, index) => ({
          number: index + 1,
          width: firstViewport.width,
          height: firstViewport.height,
        }));
        this.loading = false;
        await this.$nextTick();
        this.renderVisiblePages();
      } catch (error) {
        if (
          loadVersion !== this.loadVersion ||
          error?.name === "AbortException"
        ) {
          return;
        }
        console.error("Errore nel visualizzatore PDF di Help MyStudio:", error);
        this.error = "Impossibile visualizzare il documento.";
        this.loading = false;
      }
    },
    async disposeDocument() {
      this.cancelRenderTasks();

      if (this.loadingTask) {
        try {
          await this.loadingTask.destroy();
        } catch (error) {
          // Il task potrebbe essere già terminato o annullato.
        }
        this.loadingTask = null;
      }

      if (this.pdfDocument) {
        try {
          await this.pdfDocument.destroy();
        } catch (error) {
          // Il documento potrebbe essere già stato distrutto dal loading task.
        }
        this.pdfDocument = null;
      }
    },
    async renderPage(pageNumber, version) {
      if (
        !this.pdfDocument ||
        this.renderedPages[pageNumber] ||
        this.renderingPages[pageNumber]
      ) {
        return;
      }

      const canvasRef = this.$refs[`pageCanvas${pageNumber}`];
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
      if (!canvas) return;

      this.$set(this.renderingPages, pageNumber, true);

      try {
        const page = await this.pdfDocument.getPage(pageNumber);
        if (version !== this.renderVersion) {
          page.cleanup();
          return;
        }

        const viewport = page.getViewport({ scale: this.zoom });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.35);
        const context = canvas.getContext("2d", { alpha: false });

        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, viewport.width, viewport.height);

        const renderTask = page.render({ canvasContext: context, viewport });
        this.$set(this.renderTasks, pageNumber, renderTask);
        await renderTask.promise;
        this.$delete(this.renderTasks, pageNumber);
        if (version === this.renderVersion) {
          this.$set(this.renderedPages, pageNumber, true);
        }
        page.cleanup();
      } catch (error) {
        if (error?.name !== "RenderingCancelledException") {
          console.error(`Errore rendering pagina ${pageNumber}:`, error);
        }
      } finally {
        this.$delete(this.renderTasks, pageNumber);
        this.$delete(this.renderingPages, pageNumber);
      }
    },
    cancelRenderTasks() {
      Object.values(this.renderTasks).forEach((task) => {
        try {
          task.cancel();
        } catch (error) {
          // Il task può essersi concluso mentre viene annullato.
        }
      });
      this.renderTasks = {};
      this.renderingPages = {};
      this.renderVersion += 1;
    },
    async rerender() {
      this.cancelRenderTasks();
      this.releaseRenderedCanvases();
      await this.$nextTick();
      this.renderVisiblePages();
    },
    releaseRenderedCanvases() {
      this.pages.forEach((page) => {
        const canvasRef = this.$refs[`pageCanvas${page.number}`];
        const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
        if (!canvas) return;
        canvas.width = 1;
        canvas.height = 1;
        canvas.style.width = "0";
        canvas.style.height = "0";
      });
      this.renderedPages = {};
    },
    pageStyle(page) {
      return {
        width: `${page.width * this.zoom}px`,
        height: `${page.height * this.zoom}px`,
      };
    },
    renderVisiblePages() {
      const scrollArea = this.$refs.scrollArea;
      if (!scrollArea || !this.pdfDocument) return;

      const areaRect = scrollArea.getBoundingClientRect();
      const preloadDistance = Math.max(areaRect.height, 500);
      const minTop = areaRect.top - preloadDistance;
      const maxBottom = areaRect.bottom + preloadDistance;
      const version = this.renderVersion;

      this.pages.forEach((page) => {
        const wrapRef = this.$refs[`pageWrap${page.number}`];
        const pageWrap = Array.isArray(wrapRef) ? wrapRef[0] : wrapRef;
        if (!pageWrap) return;

        const pageRect = pageWrap.getBoundingClientRect();
        if (pageRect.bottom >= minTop && pageRect.top <= maxBottom) {
          this.renderPage(page.number, version);
        }
      });
    },
    zoomIn() {
      this.zoom = Math.min(Number((this.zoom + 0.1).toFixed(1)), 2.2);
      this.rerender();
    },
    zoomOut() {
      this.zoom = Math.max(Number((this.zoom - 0.1).toFixed(1)), 0.6);
      this.rerender();
    },
    goToPage(pageNumber) {
      const nextPage = Math.min(Math.max(pageNumber, 1), this.totalPages || 1);
      const wrapRef = this.$refs[`pageWrap${nextPage}`];
      const pageWrap = Array.isArray(wrapRef) ? wrapRef[0] : wrapRef;
      const scrollArea = this.$refs.scrollArea;
      if (pageWrap && scrollArea) {
        this.renderPage(nextPage, this.renderVersion);
        scrollArea.scrollTo({
          top: Math.max(pageWrap.offsetTop - 12, 0),
          behavior: "smooth",
        });
      }
      this.currentPage = nextPage;
    },
    handleScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.updateCurrentPage();
        this.renderVisiblePages();
        this.scrollTicking = false;
      });
    },
    updateCurrentPage() {
      const scrollArea = this.$refs.scrollArea;
      if (!scrollArea) return;

      const referenceTop = scrollArea.getBoundingClientRect().top + 12;
      let closestPage = this.currentPage;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.pages.forEach((page) => {
        const wrapRef = this.$refs[`pageWrap${page.number}`];
        const pageWrap = Array.isArray(wrapRef) ? wrapRef[0] : wrapRef;
        if (!pageWrap) return;

        const distance = Math.abs(
          pageWrap.getBoundingClientRect().top - referenceTop
        );
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = page.number;
        }
      });

      this.currentPage = closestPage;
    },
    async toggleFullscreen() {
      const viewer = this.$refs.viewer;
      if (!viewer) return;

      try {
        if (document.fullscreenElement === viewer) {
          await document.exitFullscreen();
        } else if (viewer.requestFullscreen) {
          await viewer.requestFullscreen();
        }
      } catch (error) {
        console.error(
          "Impossibile cambiare la modalità schermo intero:",
          error
        );
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = document.fullscreenElement === this.$refs.viewer;
      this.$nextTick(() => this.renderVisiblePages());
    },
  },
};
</script>

<style scoped>
.help-pdf-viewer {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  min-height: 20rem;
  overflow: hidden;
  border: 0;
  background: #000000;
  color: #18304a;
  outline: none;
  user-select: none;
}

.help-pdf-viewer--fullscreen,
.help-pdf-viewer:fullscreen {
  width: 100vw;
  height: 100vh;
  background: #000000;
}

.help-pdf-toolbar {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 3;
  display: grid;
  height: 3rem;
  padding: 0.35rem 0.6rem;
  align-items: center;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 0.8rem;
  box-sizing: border-box;
  border-bottom: 1px solid #d8dfe5;
  background: #f7f9fa;
  box-shadow: 0 1px 4px rgba(25, 47, 68, 0.08);
}

.help-pdf-title,
.help-pdf-navigation,
.help-pdf-actions {
  display: inline-flex;
  min-width: 0;
  align-items: center;
}

.help-pdf-title {
  align-self: center;
  justify-self: start;
  gap: 0.5rem;
  color: #44566a;
  font-size: 0.75rem;
  font-weight: 800;
  text-align: left;
}

.help-pdf-title-icon {
  display: inline-flex;
  width: 1.35rem;
  height: 1.8rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 1.35rem;
  color: #a64b50;
  font-size: 0.95rem;
}

.help-pdf-title-copy {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
}

.help-pdf-title-copy strong,
.help-pdf-title-copy small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.help-pdf-title-copy strong {
  color: #728195;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.2;
  text-transform: uppercase;
}

.help-pdf-title-copy small {
  margin-top: 0.1rem;
  color: #18304a;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.2;
}

.help-pdf-navigation {
  justify-content: center;
  gap: 0.25rem;
}

.help-pdf-actions {
  justify-content: flex-end;
  gap: 0.25rem;
}

.help-pdf-toolbar button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #40556b;
  cursor: pointer;
  font: inherit;
}

.help-pdf-toolbar button:hover:not(:disabled) {
  background: #e7ebee;
  color: #142c45;
}

.help-pdf-toolbar .help-pdf-close:hover:not(:disabled) {
  background: #f4e5e6;
  color: #9a3e45;
}

.help-pdf-toolbar button:focus-visible {
  outline: 2px solid #8094a8;
  outline-offset: 1px;
}

.help-pdf-toolbar button:disabled {
  opacity: 0.32;
  cursor: default;
}

.help-pdf-page-number,
.help-pdf-zoom {
  display: inline-flex;
  min-width: 2.15rem;
  height: 1.8rem;
  padding: 0 0.4rem;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1px solid #d7dfe5;
  border-radius: 5px;
  background: #ffffff;
  color: #2e4359;
  font-size: 0.72rem;
  font-weight: 800;
}

.help-pdf-page-total {
  margin-right: 0.15rem;
  color: #67778a;
  font-size: 0.72rem;
  font-weight: 700;
}

.help-pdf-zoom {
  min-width: 3.15rem;
}

.help-pdf-divider {
  width: 1px;
  height: 1.5rem;
  margin: 0 0.3rem;
  background: #d5dde3;
}

.help-pdf-scroll-area {
  position: absolute;
  top: 3rem;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: auto;
  overscroll-behavior: contain;
  background: #000000;
}

.help-pdf-pages {
  display: flex;
  width: max-content;
  min-width: 100%;
  padding: 0.9rem 1rem 2rem;
  align-items: center;
  flex-direction: column;
  gap: 1rem;
  box-sizing: border-box;
}

.help-pdf-page {
  overflow: hidden;
  flex: 0 0 auto;
  background: #ffffff;
  box-shadow: 0 3px 12px rgba(26, 44, 60, 0.2);
}

.help-pdf-page canvas {
  display: block;
  pointer-events: none;
}

.help-pdf-status {
  display: flex;
  height: 100%;
  min-height: 16rem;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  color: #526477;
  font-size: 0.8rem;
  font-weight: 800;
}

.help-pdf-status--error {
  color: #8b4348;
}

.help-pdf-spinner {
  width: 1.25rem;
  height: 1.25rem;
  box-sizing: border-box;
  border: 2px solid rgba(70, 91, 111, 0.25);
  border-top-color: #465b6f;
  border-radius: 50%;
  animation: help-pdf-spin 0.75s linear infinite;
}

@keyframes help-pdf-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 720px) {
  .help-pdf-toolbar {
    grid-template-columns: 1fr auto;
  }

  .help-pdf-title {
    display: none;
  }

  .help-pdf-navigation {
    justify-content: flex-start;
  }

  .help-pdf-pages {
    align-items: flex-start;
  }
}
</style>
